// Loads a source image (or accepts a canvas/video) and provides cell-grid
// sampling: average color per cell. Used to drive the letter mosaic.

export class SourceSampler {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.width = 0;
    this.height = 0;
    this.imageData = null;
    this.aspect = 1;
  }

  async loadImage(url) {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = (e) => reject(new Error(`load fail: ${url}`));
      im.src = url;
    });
    this.setSource(img, img.naturalWidth, img.naturalHeight);
    return this;
  }

  // Source can be HTMLImageElement, HTMLCanvasElement, HTMLVideoElement, etc.
  setSource(source, w, h) {
    // Downscale into our internal canvas at modest resolution; we don't need
    // pixel-perfect sampling, just per-cell averages.
    const maxDim = 512;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    this.width = Math.max(1, Math.floor(w * scale));
    this.height = Math.max(1, Math.floor(h * scale));
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.ctx.drawImage(source, 0, 0, this.width, this.height);
    this.imageData = this.ctx.getImageData(0, 0, this.width, this.height);
    this.aspect = this.width / this.height;
  }

  // Returns {r, g, b, lum} in [0,1] sampled at normalized (u, v) with v=0 at top
  sample(u, v) {
    if (!this.imageData) return { r: 0, g: 0, b: 0, lum: 0 };
    const x = Math.max(0, Math.min(this.width - 1, Math.floor(u * this.width)));
    const y = Math.max(0, Math.min(this.height - 1, Math.floor(v * this.height)));
    const idx = (y * this.width + x) * 4;
    const r = this.imageData.data[idx + 0] / 255;
    const g = this.imageData.data[idx + 1] / 255;
    const b = this.imageData.data[idx + 2] / 255;
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return { r, g, b, lum };
  }
}
